import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const ImpersonationBanner: React.FC = () => {
  const { impersonationStatus, stopImpersonation, isLoading } = useImpersonation();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  if (!impersonationStatus?.is_impersonating) {
    return null;
  }

  const handleStopImpersonation = async () => {
    setShowConfirmModal(false);
    try {
      await stopImpersonation();
      // Optionally reload the page to refresh all permissions
      window.location.reload();
    } catch (error) {
      alert('Failed to stop impersonation. Please try again.');
    }
  };

  return (
    <>
      <div className="bg-yellow-500 text-black px-4 py-2 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <span className="font-semibold">
              Impersonating: {impersonationStatus.impersonated_email}
            </span>
            <span className="text-sm opacity-75">
              (You are viewing as this user)
            </span>
          </div>

          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={isLoading}
            className="bg-black text-yellow-500 px-4 py-1 rounded font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Stopping...' : 'Exit Impersonation'}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm">
          <div className="bg-panel rounded-lg shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-heading mb-2">
                  Exit Impersonation?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Are you sure you want to stop impersonating <span className="font-semibold">{impersonationStatus.impersonated_email}</span>?
                  The page will reload to restore your original permissions.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleStopImpersonation}
                    className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-md transition-colors"
                  >
                    Yes, Exit Impersonation
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ImpersonationBanner;
